import { useMemo, useState } from 'react'
import { Stack } from 'react-bootstrap'
import { useIntegrationRequirement } from '../../common/useIntegrationRequirement'
import { ReactComponent as ZoomIcon } from '../../assets/images/integration-logos/color/zoom-logo.svg'
import { ReactComponent as ZocksIcon } from '../../assets/images/ZocksIcon-primary.svg'
import { IntegrationServiceId } from '../../common/integrations'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { useGetMeQuery } from '../../store/user/user'
import { Button } from '../Button/Button'
import { LinkButton } from '../Button/LinkButton'
import { ZoomOBFWarningModal } from './ZoomOBFWarningModal'

import './ZoomOBFWarningOverlay.scss'

const LOCAL_STORAGE_KEY = 'zoomOBFWarningDismissed'

const NYL_PARENT_ACCOUNT_ID = 'cksk89es4htp5kmj' // NYL Home Office

const ZOOM_NO_JOIN_SENTENCE = `Due to Zoom's updated security policy, the AI Assistant will no longer be able to join Zocks meetings.`

export const ZoomOBFWarningOverlay: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(LOCAL_STORAGE_KEY))
  const [modalVisible, setModalVisible] = useState(false)
  const checkIntegrationRequirement = useIntegrationRequirement()
  const { data: currentUser } = useGetMeQuery({ accountId }, { skip: dismissed })
  const { data: account } = useGetAccountQuery({ accountId }, { skip: dismissed })

  const zoomIntegrationStatus = useMemo(() => {
    return currentUser?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ZOOM)?.state ?? 'notConnected'
  }, [currentUser])

  // Only warn when the Zoom requirement is unmet. When strict-join (companion mode) is neither
  // enterprise-enforced nor user-opted-in (and the feature quota is on), the bot falls back to the
  // ZAK token, so no live integration is required and the overlay is unnecessary.
  const { satisfied: zoomRequirementSatisfied, accountAllowed: zoomAllowed } = checkIntegrationRequirement(IntegrationServiceId.ZOOM)

  const shouldShowOverlay = useMemo(
    () => !dismissed && zoomAllowed && !zoomRequirementSatisfied && currentUser && zoomIntegrationStatus !== 'live' && !!account,
    [account, currentUser, dismissed, zoomAllowed, zoomRequirementSatisfied, zoomIntegrationStatus],
  )

  const isNYL = accountId === NYL_PARENT_ACCOUNT_ID || account?.data.parentId === NYL_PARENT_ACCOUNT_ID

  const overlayHeader =
    zoomIntegrationStatus === 'notConnected'
      ? isNYL
        ? 'Connect Zoom to Maintain AI Meeting Support'
        : 'Your AI Assistant will stop working in Zoom meetings'
      : isNYL
        ? 'Reconnect Zoom to Maintain AI Meeting Support'
        : 'Your Zoom integration has expired'

  const overlayBody =
    zoomIntegrationStatus === 'notConnected' ? (
      isNYL ? (
        <p className='mb-0 paragraph-small text-center zocks-text-gray-100'>
          NYL has enabled the Zoom integration with Zocks. Connect Zoom now to ensure your AI Assistant continues supporting your meetings seamlessly.
          <br /> {ZOOM_NO_JOIN_SENTENCE}
        </p>
      ) : (
        <p className='mb-0 paragraph-small text-center zocks-text-gray-100'>{ZOOM_NO_JOIN_SENTENCE}</p>
      )
    ) : isNYL ? (
      <p className='mb-0 paragraph-small text-center zocks-text-gray-100'>
        Your Zoom integration has expired. Reconnect Zoom now to ensure your AI Assistant continues supporting your meetings seamlessly. <br />
        {ZOOM_NO_JOIN_SENTENCE}
      </p>
    ) : (
      <p className='mb-0 paragraph-small text-center zocks-text-gray-100'>
        Your Zoom integration has expired and Zoom now requires active integration for meeting access.
      </p>
    )

  const handleClose = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, 'true')
    setDismissed(true)
  }

  if (!shouldShowOverlay) {
    return null
  }

  return (
    <>
      <ZoomOBFWarningModal zoomNotConnected={zoomIntegrationStatus === 'notConnected'} show={modalVisible} onHide={() => setModalVisible(false)} />
      <div className='zoom-obf-warning position-fixed bottom-0 start-50 translate-middle-x zocks-border-0.75 p-3 mb-3 z-3 position-relative'>
        <button type='button' className='btn-close btn-close-white position-absolute top-0 end-0 m-3' aria-label='Close' onClick={handleClose} />
        <div className='d-flex flex-column gap-3'>
          <div className='d-flex justify-content-center align-items-center mb-2'>
            <div className='d-flex align-items-center justify-content-center gap-1'>
              <div className='d-flex align-items-center justify-content-center bg-white zocks-border-0.5 icon-wrapper'>
                <ZoomIcon className='icon' />
              </div>
              <Stack direction='vertical' gap={1} className='d-flex justify-content-center align-items-center'>
                <div className='gradient-bar-ltr' />
                <div className='gradient-bar-rtl' />
              </Stack>
              <div className='d-flex align-items-center justify-content-center bg-white zocks-border-0.5 icon-wrapper'>
                <ZocksIcon className='icon' />
              </div>
            </div>
          </div>
          <h6 className='zocks-text-gray-100 text-center'>
            Action Required:
            <br />
            {overlayHeader}
          </h6>
          {overlayBody}
          <div className='d-flex gap-3 justify-content-center'>
            <Button variant='secondary' onClick={() => setModalVisible(true)}>
              Read more
            </Button>
            <LinkButton variant='primary' to='/account/integration-settings/integrations/zoom'>
              {zoomIntegrationStatus === 'notConnected' ? 'Connect Zoom' : 'Reconnect Zoom'}
            </LinkButton>
          </div>
        </div>
      </div>
    </>
  )
}
